import React, { useState, useEffect, useCallback } from 'react';
import api from '@/config/axios';
import { useAuth } from '../contexts/AuthContext';
import { useToastContext } from '../ui/feedback/ToastProvider';
import { Modal, Spinner, Button } from '../ui';
import DirhamSymbol from './DirhamSymbol';
import {
    TrendingUp,
    DollarSign,
    Clock,
    CheckCircle,
    RotateCcw,
    RefreshCw,
} from 'lucide-react';

const TimelineEvent = ({ event, isLast, onClick, isSelected }) => {
    // Custom icons for timeline steps
    const getIcon = () => {
        switch (event.stepKey) {
            case 'startTime': return <Clock size={16} className="text-primary-600 dark:text-primary-400" />;
            case 'usdtTime': return <DollarSign size={16} className="text-secondary-600 dark:text-secondary-400" />;
            case 'usdtToAedTime': return <RotateCcw size={16} className="text-secondary-600 dark:text-secondary-400" />;
            case 'transferToUaeTime': return <TrendingUp size={16} className="text-success-600 dark:text-success-400" />;
            case 'endTime': return <CheckCircle size={16} className="text-success-600 dark:text-success-400" />;
            default: return <Clock size={16} className="text-gray-500" />;
        }
    };

    const getBgColor = () => {
        return 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700';
    };

    return (
        <div className="relative pl-6 pb-6 last:pb-0">
            {/* Vertical Line */}
            {!isLast && (
                <div className="absolute left-[11px] top-6 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700" />
            )}

            {/* Icon Node */}
            <div className={`absolute left-0 top-1 w-6 h-6 rounded-full flex items-center justify-center border-2 bg-white dark:bg-gray-900 z-10 ${event.isCompleted ? 'border-primary-500' : 'border-gray-300 dark:border-gray-600'
                }`}>
                <div className={`w-2 h-2 rounded-full ${event.isCompleted ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`} />
            </div>

            {/* Content */}
            <div
                onClick={() => onClick(event)}
                className={`group cursor-pointer rounded-lg p-3 transition-all duration-200 hover:shadow-md border ${getBgColor()} ${isSelected ? 'ring-2 ring-offset-2 ring-primary-500 dark:ring-offset-gray-900' : ''}`}
            >
                <div className="flex items-start justify-between gap-2">
                    <div>
                        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-0.5 block">
                            {event.stepKey === 'startTime' ? new Date(event.timelineDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : event.title}
                        </span>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                            {getIcon()}
                            {event.stepKey === 'startTime' ? event.title : event.title}
                        </h4>
                        <div className="mt-2 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                            <Clock size={12} />
                            {new Date(event.date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const InvestorTimeline = () => {
    const { user } = useAuth();
    const { error: showError, success: showSuccess } = useToastContext();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [activeDate, setActiveDate] = useState(null);

    const loadTimelineData = useCallback(async ({ showSpinner = true } = {}) => {
        if (!user || user.role !== 'investor') return;

        if (showSpinner) setLoading(true);
        try {
            const response = await api.get('/api/timeline/all', {
                params: { limit: 20, _t: Date.now() },
            });
            const timelines = response.data.timelines || [];

            if (timelines.length === 0) {
                setEvents([]);
                setActiveDate(null);
                return;
            }

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            let activeTimeline = timelines.find(t => {
                const tDate = new Date(t.date);
                tDate.setHours(0, 0, 0, 0);
                return tDate.getTime() === today.getTime();
            });

            if (!activeTimeline && timelines.length > 0) {
                activeTimeline = timelines[0];
            }

            if (activeTimeline) {
                setActiveDate(activeTimeline.date);
                const timelineEvents = [];

                const steps = [
                    { key: 'startTime', label: 'Transaction Started', time: activeTimeline.startTime },
                    { key: 'usdtTime', label: 'USDT Buy', time: activeTimeline.usdtTime },
                    { key: 'usdtToAedTime', label: 'AED Convert', time: activeTimeline.usdtToAedTime },
                    { key: 'transferToUaeTime', label: 'UAE Transfer', time: activeTimeline.transferToUaeTime },
                    { key: 'endTime', label: 'Transaction Completed', time: activeTimeline.endTime }
                ];

                const usdtAmt = Number(
                    activeTimeline.investedAmountUsdt != null
                        ? activeTimeline.investedAmountUsdt
                        : activeTimeline.investedAmount || 0
                );
                const rate =
                    Number(activeTimeline.aedConversionRate) ||
                    parseFloat(import.meta.env.VITE_USD_TO_AED_RATE) ||
                    3.662;
                const aedAmt = Number(
                    activeTimeline.investedAmountAed != null
                        ? activeTimeline.investedAmountAed
                        : usdtAmt * rate
                );

                steps.forEach((step) => {
                    if (step.time) {
                        timelineEvents.push({
                            id: `${activeTimeline._id}-${step.key}`,
                            timelineId: activeTimeline._id,
                            timelineDate: activeTimeline.date,
                            type: 'timeline-step',
                            stepKey: step.key,
                            title: step.label,
                            date: new Date(step.time),
                            isCompleted: true,
                            investedAmountUsdt: usdtAmt,
                            investedAmountAed: aedAmt,
                            aedConversionRate: rate,
                            details: {
                                status: activeTimeline.status,
                                notes: activeTimeline.notes,
                            },
                        });
                    }
                });

                timelineEvents.sort((a, b) => a.date - b.date);
                setEvents(timelineEvents);
            }
        } catch (error) {
            console.error('Error fetching timeline data:', error);
        } finally {
            if (showSpinner) setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        loadTimelineData({ showSpinner: true });
    }, [loadTimelineData]);

    const handleRefreshTimeline = async () => {
        try {
            setRefreshing(true);
            await api.post('/api/timeline/refresh', {});
            await loadTimelineData({ showSpinner: false });
            showSuccess('Timeline updated');
        } catch (error) {
            console.error('Error refreshing timeline:', error);
            showError(error.response?.data?.message || 'Failed to refresh timeline');
        } finally {
            setRefreshing(false);
        }
    };

    if (!user || user.role !== 'investor') return null;

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <Spinner size="sm" />
            </div>
        );
    }

    return (
        <>
            <div className="mt-6 px-4 pb-20">
                <div className="flex items-start justify-between gap-2 mb-4 px-2 sticky top-0 bg-accent-50 dark:bg-black z-20 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                        <Clock size={16} className="text-primary-500 shrink-0" />
                        <div className="min-w-0">
                            <h3 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Latest Activity
                            </h3>
                            {activeDate && (
                                <p className="text-[10px] text-gray-400 truncate">
                                    {new Date(activeDate).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                                </p>
                            )}
                        </div>
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0 text-xs px-2 py-1 h-8"
                        onClick={handleRefreshTimeline}
                        disabled={refreshing}
                        leftIcon={<RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />}
                    >
                        Refresh
                    </Button>
                </div>

                {events.length === 0 ? (
                    <div className="px-2 text-center text-sm text-gray-500 dark:text-gray-400 space-y-2">
                        <p>No timeline activity yet.</p>
                        <p className="text-xs">Tap <strong>Refresh</strong> to generate today&apos;s activity.</p>
                    </div>
                ) : (

                <div className="relative border-l-2 border-dashed border-gray-200 dark:border-gray-800 ml-3 pl-6 pr-2">
                    {events.map((event, index) => (
                        <div key={event.id} className="relative mb-6 last:mb-0">
                            {/* Dot on line */}
                            <div className={`absolute -left-[31px] top-1 w-3 h-3 rounded-full border-2 border-white dark:border-black z-10 ${'bg-primary-500' // Default color
                                }`} />

                            <div
                                onClick={() => setSelectedEvent(event)}
                                className={`group cursor-pointer rounded-lg p-3 text-sm transition-all hover:bg-white dark:hover:bg-gray-800 border border-transparent hover:border-gray-200 dark:hover:border-gray-700 hover:shadow-sm ${selectedEvent?.id === event.id ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm' : ''
                                    }`}
                            >
                                <div className="flex justify-between items-start mb-1">
                                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                                        {new Date(event.date).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
                                    </span>
                                    <span className="text-[10px] text-gray-400 ml-1">
                                        {event.date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>

                                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-0.5 truncate">
                                    {event.title}
                                </h4>

                                {(event.investedAmountUsdt > 0 || event.investedAmountAed > 0) && (
                                    <div className="mt-1 text-xs text-secondary-600 dark:text-secondary-400 flex flex-col gap-0.5 font-medium">
                                        {(event.stepKey === 'startTime' || event.stepKey === 'usdtTime') ? (
                                            <>
                                                <span className="flex items-center gap-1">
                                                    <DollarSign size={10} className="stroke-[3]" />
                                                    {Number(event.investedAmountUsdt || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    <span className="text-[10px] font-normal text-gray-500 dark:text-gray-400">USDT</span>
                                                </span>
                                                <span className="flex items-center gap-1 text-[10px] font-normal text-gray-500 dark:text-gray-400">
                                                    <DirhamSymbol size={10} inline />
                                                    {Number(event.investedAmountAed || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    <span>AED (equiv.)</span>
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <span className="flex items-center gap-1">
                                                    <DirhamSymbol size={10} inline />
                                                    {Number(event.investedAmountAed || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    <span className="text-[10px] font-normal text-gray-500 dark:text-gray-400">AED</span>
                                                </span>
                                                <span className="flex items-center gap-1 text-[10px] font-normal text-gray-500 dark:text-gray-400">
                                                    <DollarSign size={10} className="stroke-[3]" />
                                                    {Number(event.investedAmountUsdt || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    <span>USDT (equiv.)</span>
                                                </span>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                )}
            </div>

            <Modal
                isOpen={!!selectedEvent}
                onClose={() => setSelectedEvent(null)}
                title={selectedEvent?.title}
                size="sm"
            >
                {selectedEvent && (
                    <div className="space-y-4">
                        <div className="text-center">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                {selectedEvent.title}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {new Date(selectedEvent.date).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} at {new Date(selectedEvent.date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                            </p>
                        </div>

                        {(selectedEvent.investedAmountUsdt > 0 || selectedEvent.investedAmountAed > 0) && (
                            <div className="space-y-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">USDT (invested)</span>
                                    <span className="text-sm font-bold text-gray-900 dark:text-gray-100 flex items-center gap-1">
                                        <DollarSign size={14} className="stroke-[3]" />
                                        {Number(selectedEvent.investedAmountUsdt || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">AED (converted)</span>
                                    <span className="text-sm font-bold text-gray-900 dark:text-gray-100 flex items-center gap-1">
                                        <DirhamSymbol size={14} inline />
                                        {Number(selectedEvent.investedAmountAed || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                </div>
                                {selectedEvent.aedConversionRate ? (
                                    <p className="text-[10px] text-gray-500 dark:text-gray-400 text-center">
                                        Rate: 1 USDT ≈ {Number(selectedEvent.aedConversionRate).toFixed(4)} AED
                                    </p>
                                ) : null}
                            </div>
                        )}

                        {selectedEvent.details?.notes && (
                            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm text-gray-600 dark:text-gray-300 italic">
                                Note: {selectedEvent.details.notes}
                            </div>
                        )}


                    </div>
                )}
            </Modal>
        </>
    );
};

export default InvestorTimeline;
